import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Hotel, Loader2, Save, GripVertical, Download, Upload, Plus, Trash2, CheckSquare, Square, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, arrayMove, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('token') || localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// ── Sortable row for the export configuration list ──────────────────────────
const SortableField = ({ item, toggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.key });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`export-field-row-${item.key}`}
      className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${item.enabled ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100'}`}>
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600" data-testid={`export-field-drag-${item.key}`}>
        <GripVertical size={16} />
      </button>
      <button onClick={() => toggle(item.key)} data-testid={`export-field-toggle-${item.key}`} className="text-teal-600">
        {item.enabled ? <CheckSquare size={18} /> : <Square size={18} className="text-gray-300" />}
      </button>
      <span className={`flex-1 text-sm ${item.enabled ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>{item.label}</span>
      <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{item.group}</span>
    </div>
  );
};

const ExportConfiguration = ({ registry }) => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/settings/property-export-config`, { headers: authHeader() });
        const cfg = res.ok ? await res.json() : { fields: [] };
        const regByKey = Object.fromEntries(registry.map((f) => [f.key, f]));
        const saved = (cfg.fields || []).filter((c) => regByKey[c.key]);
        const savedKeys = new Set(saved.map((c) => c.key));
        // Saved order first, then any new registry fields (enabled by default).
        const merged = [
          ...saved.map((c) => ({ ...regByKey[c.key], enabled: c.enabled !== false })),
          ...registry.filter((f) => !savedKeys.has(f.key)).map((f) => ({ ...f, enabled: true })),
        ];
        setItems(merged);
      } catch (e) { toast.error('Failed to load export config'); }
      finally { setLoading(false); }
    })();
  }, [registry]);

  const toggle = (key) => setItems((prev) => prev.map((i) => (i.key === key ? { ...i, enabled: !i.enabled } : i)));
  const setAll = (val) => setItems((prev) => prev.map((i) => ({ ...i, enabled: val })));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const oldIdx = prev.findIndex((i) => i.key === active.id);
      const newIdx = prev.findIndex((i) => i.key === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const fields = items.map((i) => ({ key: i.key, enabled: i.enabled }));
      const res = await fetch(`${API}/api/settings/property-export-config`, {
        method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      toast.success('Export configuration saved');
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const selectedCount = items.filter((i) => i.enabled).length;

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4" data-testid="property-export-config">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2"><Download size={18} className="text-teal-600" /> Property Export Configuration</h3>
          <p className="text-sm text-gray-500 mt-0.5">Choose which Master-Form fields to export and drag to reorder. {selectedCount} of {items.length} selected.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setAll(true)} className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="export-select-all">Select All</button>
          <button onClick={() => setAll(false)} className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50" data-testid="export-deselect-all">Deselect All</button>
          <button onClick={save} disabled={saving} className="text-sm px-4 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="export-config-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      </div>
      <div className="max-h-[520px] overflow-y-auto pr-1 space-y-1.5">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={items.map((i) => i.key)} strategy={verticalListSortingStrategy}>
            {items.map((item) => <SortableField key={item.key} item={item} toggle={toggle} />)}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

// ── Import mapping ──────────────────────────────────────────────────────────
const ImportConfiguration = ({ registry }) => {
  const [rows, setRows] = useState([]); // [{column, field_key}]
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);

  const groupedOptions = useMemo(() => {
    const groups = {};
    registry.forEach((f) => { (groups[f.group] = groups[f.group] || []).push(f); });
    return groups;
  }, [registry]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/settings/property-import-mapping`, { headers: authHeader() });
        const cfg = res.ok ? await res.json() : { mappings: [] };
        setRows((cfg.mappings || []).length ? cfg.mappings : [{ column: '', field_key: '' }]);
      } catch (e) { toast.error('Failed to load import mapping'); }
      finally { setLoading(false); }
    })();
  }, []);

  const detectColumns = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDetecting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/properties/import/columns`, { method: 'POST', headers: authHeader(), body: fd });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to read columns');
      const { columns } = await res.json();
      // Merge detected columns into rows (preserve existing field_key when column matches).
      setRows((prev) => {
        const byCol = Object.fromEntries(prev.map((r) => [r.column.toLowerCase().trim(), r.field_key]));
        return columns.map((c) => ({ column: c, field_key: byCol[c.toLowerCase().trim()] || '' }));
      });
      toast.success(`Detected ${columns.length} columns`);
    } catch (err) { toast.error(err.message); } finally { setDetecting(false); e.target.value = ''; }
  };

  const updateRow = (idx, patch) => setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const addRow = () => setRows((prev) => [...prev, { column: '', field_key: '' }]);
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const mappings = rows.filter((r) => r.column.trim() && r.field_key);
      const res = await fetch(`${API}/api/settings/property-import-mapping`, {
        method: 'PUT', headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ mappings }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      toast.success('Import mapping saved');
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-4" data-testid="property-import-config">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-gray-800 flex items-center gap-2"><Upload size={18} className="text-teal-600" /> Property Import Configuration</h3>
          <p className="text-sm text-gray-500 mt-0.5">Map your Excel/CSV columns to Master-Form fields. Saved mappings apply automatically on import.</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer inline-flex items-center gap-1.5" data-testid="import-detect-columns">
            {detecting ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />} Detect from file
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={detectColumns} />
          </label>
          <button onClick={save} disabled={saving} className="text-sm px-4 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="import-mapping-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      </div>

      <div className="border border-gray-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 px-4 py-2.5 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          <span>Excel Column</span><span></span><span>Master Form Field</span><span></span>
        </div>
        <div className="divide-y divide-gray-100">
          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_auto_1fr_auto] gap-2 items-center px-4 py-2" data-testid={`import-map-row-${idx}`}>
              <input value={row.column} onChange={(e) => updateRow(idx, { column: e.target.value })}
                placeholder="Excel column name" data-testid={`import-map-column-${idx}`}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-200 outline-none" />
              <span className="text-gray-400 text-sm">→</span>
              <select value={row.field_key} onChange={(e) => updateRow(idx, { field_key: e.target.value })}
                data-testid={`import-map-field-${idx}`}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-200 outline-none">
                <option value="">— Select field —</option>
                {Object.entries(groupedOptions).map(([group, fields]) => (
                  <optgroup key={group} label={group}>
                    {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                  </optgroup>
                ))}
              </select>
              <button onClick={() => removeRow(idx)} className="text-gray-400 hover:text-red-500" data-testid={`import-map-remove-${idx}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </div>
      <button onClick={addRow} className="text-sm text-teal-600 hover:text-teal-700 inline-flex items-center gap-1.5" data-testid="import-map-add-row">
        <Plus size={15} /> Add mapping row
      </button>
    </div>
  );
};

const PropertiesSettings = () => {
  const [registry, setRegistry] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('export');

  const loadRegistry = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/properties/field-registry`, { headers: authHeader() });
      const data = res.ok ? await res.json() : { fields: [] };
      setRegistry(data.fields || []);
    } catch (e) { toast.error('Failed to load property fields'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadRegistry(); }, [loadRegistry]);

  return (
    <div className="space-y-6" data-testid="properties-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
          <Hotel size={28} className="text-teal-600" /> Properties Settings
        </h1>
        <p className="text-gray-500 mt-1">Configure the complete Property Master-Form export and import field mapping.</p>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab('export')} data-testid="properties-tab-export"
          className={`px-4 py-2 text-sm font-semibold rounded-lg border ${tab === 'export' ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
          Export Configuration
        </button>
        <button onClick={() => setTab('import')} data-testid="properties-tab-import"
          className={`px-4 py-2 text-sm font-semibold rounded-lg border ${tab === 'import' ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
          Import Mapping
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" size={28} /></div>
        ) : tab === 'export' ? (
          <ExportConfiguration registry={registry} />
        ) : (
          <ImportConfiguration registry={registry} />
        )}
      </div>
    </div>
  );
};

export default PropertiesSettings;
